import type { FC } from 'react';
import { useHasPermission } from '@src/shared/hooks/use-has-permission';
import { entities } from '@src/shared/constants/entities';
import {
  EditableDropdownCell,
  EditableDropdownCellEditMode,
  EditableDropdownCellViewMode,
} from '@src/shared/components/editable-dropdown-cell';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { LEAD_NOTE_STATUS, type TLeadNote } from '@src/shared/types/lead-note';
import leadNoteApi from '@src/features/leads/services/lead-note-api';
import type { ValueOf } from '@src/shared/types/utilities';
import { DropdownMenuItem } from '@src/design-system/dropdown-menu';
import { cn } from '@src/design-system/helpers';
import { CloseCircle, TickCircle } from 'iconsax-react';

const STATUS_COLORS: Record<ValueOf<typeof LEAD_NOTE_STATUS>, string> = {
  [LEAD_NOTE_STATUS.today]: 'bg-yellow-100 text-yellow-800',
  [LEAD_NOTE_STATUS.overdue]: 'bg-red-100 text-red-800',
  [LEAD_NOTE_STATUS.complete]: 'bg-gray-100 text-gray-900',
  [LEAD_NOTE_STATUS.upcoming]: 'bg-green-100 text-green-800',
};

export const FollowUpStatusCell: FC<{
  status: ValueOf<typeof LEAD_NOTE_STATUS>;
  note: TLeadNote;
}> = ({ status, note }) => {
  const { variables, error } = leadNoteApi.useUpdate();
  const { mutateAsync: resolveNote } = leadNoteApi.useResolveLeadNote();
  const { mutateAsync: unresolveNote } = leadNoteApi.useUnresolveLeadNote();
  const optimisticStatus = error
    ? status
    : (variables?.next_follow_up_date ?? status);

  const hasPermission = useHasPermission();
  const canEditLead = hasPermission(entities.lead, 'change');
  const colorClasses =
    (optimisticStatus &&
      Object.prototype.hasOwnProperty.call(STATUS_COLORS, optimisticStatus) &&
      STATUS_COLORS[optimisticStatus as ValueOf<typeof LEAD_NOTE_STATUS>]) ||
    '';

  if (!canEditLead || !note.next_follow_up_date) {
    return (
      <span
        className={`justify-start gap-2 p-0 border-none text-sm font-normal capitalize px-[10px] py-1 rounded-[8px] ${colorClasses}`}
      >
        {formatTextForDisplay(optimisticStatus ?? '-').replace('_', ' ')}
      </span>
    );
  }

  return (
    <EditableDropdownCell>
      <EditableDropdownCellViewMode>
        <span
          className={`px-[10px] py-1 block rounded-[8px] text-sm ${colorClasses}`}
        >
          {formatTextForDisplay(optimisticStatus ?? '-')}
        </span>
      </EditableDropdownCellViewMode>
      <EditableDropdownCellEditMode>
        <DropdownMenuItem
          className={cn(
            'flex items-center gap-1 min-w-0',
            note.is_resolved && 'bg-primary-50 text-primary-1000'
          )}
          onClick={() => {
            if (note.id) {
              note.is_resolved ? unresolveNote(note.id) : resolveNote(note.id);
            }
          }}
        >
          {note.is_resolved ? (
            <CloseCircle className='size-4' />
          ) : (
            <TickCircle className='size-4' />
          )}
          {formatTextForDisplay(note.is_resolved ? 'UnResolve' : 'Resolve')}
        </DropdownMenuItem>
      </EditableDropdownCellEditMode>
    </EditableDropdownCell>
  );
};
